import { useCallback, useMemo, useRef } from 'react'
import isNil from 'lodash/isNil'
import type { SortOrder } from 'antd/es/table/interface'
import {
  InvoiceDeliveryItemDto,
  InvoiceTemplate,
} from '../../../api/dto/invoice-delivery.dto'
import { orderOePairs } from './invoiceTemplates'

export interface TemplateRowInfo {
  /** OE2 listed directly under its company's OE1. */
  isPairSecond: boolean
  /** Newest upload time of the OE pair, so date sorting keeps the pair together. */
  pairSortTime: number | null
}

const UNPAIRED_ROW_INFO: TemplateRowInfo = {
  isPairSecond: false,
  pairSortTime: null,
}

const uploadTime = (delivery: Pick<InvoiceDeliveryItemDto, 'createdAt'>) =>
  new Date(delivery.createdAt).getTime()

const isOeHalf = (template: InvoiceTemplate) =>
  template === InvoiceTemplate.OE_FIRST || template === InvoiceTemplate.OE_FINAL

const buildRowInfo = (
  deliveries: InvoiceDeliveryItemDto[]
): Map<string, TemplateRowInfo> => {
  const templatesByClient = new Map<string, Set<InvoiceTemplate>>()
  const latestOeUploadByClient = new Map<string, number>()
  for (const delivery of deliveries) {
    if (isNil(delivery.clientId)) continue
    const templates = templatesByClient.get(delivery.clientId) ?? new Set()
    templates.add(delivery.invoiceTemplate)
    templatesByClient.set(delivery.clientId, templates)
    if (isOeHalf(delivery.invoiceTemplate)) {
      latestOeUploadByClient.set(
        delivery.clientId,
        Math.max(
          latestOeUploadByClient.get(delivery.clientId) ?? 0,
          uploadTime(delivery)
        )
      )
    }
  }

  const info = new Map<string, TemplateRowInfo>()
  for (const delivery of deliveries) {
    const clientTemplates = isNil(delivery.clientId)
      ? undefined
      : templatesByClient.get(delivery.clientId)
    const isPaired =
      !isNil(clientTemplates) &&
      clientTemplates.has(InvoiceTemplate.OE_FIRST) &&
      clientTemplates.has(InvoiceTemplate.OE_FINAL) &&
      isOeHalf(delivery.invoiceTemplate)
    info.set(delivery.id, {
      pairSortTime:
        isPaired && !isNil(delivery.clientId)
          ? (latestOeUploadByClient.get(delivery.clientId) ?? null)
          : null,
      isPairSecond:
        isPaired && delivery.invoiceTemplate === InvoiceTemplate.OE_FINAL,
    })
  }
  return info
}

/**
 * Upload-time sorter for the delivery tables. A listed OE pair sorts as one
 * unit at its newest upload, with OE1 above OE2 in either direction (antd
 * negates the result for descending, so the tie-break is flipped to match).
 */
export const buildUploadTimeSorter =
  (getRowInfo: (id: string) => TemplateRowInfo) =>
  (
    a: InvoiceDeliveryItemDto,
    b: InvoiceDeliveryItemDto,
    sortOrder?: SortOrder
  ): number => {
    const aInfo = getRowInfo(a.id)
    const bInfo = getRowInfo(b.id)
    const aTime = aInfo.pairSortTime ?? uploadTime(a)
    const bTime = bInfo.pairSortTime ?? uploadTime(b)
    if (aTime !== bTime) return aTime - bTime
    const isEitherPaired =
      !isNil(aInfo.pairSortTime) || !isNil(bInfo.pairSortTime)
    // Same sort time, different companies: order by company so no pair is split.
    if (isEitherPaired && a.clientId !== b.clientId) {
      return (a.clientId ?? '').localeCompare(b.clientId ?? '')
    }
    const isSamePair =
      !isNil(aInfo.pairSortTime) &&
      !isNil(bInfo.pairSortTime) &&
      a.clientId === b.clientId
    if (!isSamePair) return uploadTime(a) - uploadTime(b)
    const oe1First = a.invoiceTemplate === InvoiceTemplate.OE_FIRST ? -1 : 1
    return sortOrder === 'descend' ? -oe1First : oe1First
  }

/** Orders one month's deliveries so each OE pair sits together, with each row's pair info for styling and sorting. */
export const useTemplateRows = (deliveries: InvoiceDeliveryItemDto[]) => {
  const orderedDeliveries = useMemo(
    () => orderOePairs(deliveries),
    [deliveries]
  )
  const rowInfoById = useMemo(() => buildRowInfo(deliveries), [deliveries])

  // antd keeps the sorter closure from the table's first render (before rows
  // load), so the lookup must be stable and always read the current rows.
  const rowInfoRef = useRef(rowInfoById)
  rowInfoRef.current = rowInfoById
  const getRowInfo = useCallback(
    (id: string): TemplateRowInfo =>
      rowInfoRef.current.get(id) ?? UNPAIRED_ROW_INFO,
    []
  )

  return { orderedDeliveries, getRowInfo }
}
